<script setup>
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';
import '@/style.css';

defineProps({
  slides: { type: Array, required: true },
  title: { type: String, required: true },
});
</script>

<template>
  <div class="carousel-container background-black">
    <splide
      class="background-black"
      :options="{
        type: 'fade',
        speed: 200,
        easing: 'ease-in-out',
        autoplay: true,
        interval: 5000,
        pauseOnHover: true,
        pauseOnFocus: true,
        rewind: true,
        perPage: 1,
        autoHeight: true, // Allow natural image proportions
        cover: true,
        arrows: false,
        // Important: avoid blank frames from lazy-loading on fade transitions
        lazyLoad: false,
        preloadPages: 2,
        waitForTransition: true,
        gap: '0px'
      }"
    >
      <splide-slide
        v-for="(slide, index) in slides"
        :key="index"
      >
        <div class="slide-content hero-height">
          <!-- Force eager loading to prevent white while image decodes -->
          <img
            :src="slide.image"
            :alt="slide.alt"
            class="slide-image"
            loading="eager"
            decoding="async"
          >
          <div class="text-overlay">
            <component :is="slide.component" />
          </div>
        </div>
      </splide-slide>
    </splide>
  </div>
</template>

<style scoped>
/* Make the entire page black in case parent/layout flashes */
:global(html, body, #app) {
  background: #000 !important;
}

/* Container + every Splide layer black */
.carousel-container {
  position: relative;
  width: 100%;
  padding-top: 0;
  background: #000;
}
:deep(.splide),
:deep(.splide__track),
:deep(.splide__list),
:deep(.splide__slide) {
  background: #000 !important;
}

/* Hide until initialized to prevent FOUC */
:deep(.splide:not(.is-initialized)) {
  visibility: hidden;
}

/* Pagination/arrows: ensure no white defaults from theme */
:deep(.splide__pagination__page) { background: rgba(255,255,255,0.35); }
:deep(.splide__pagination__page.is-active) { background: #fff; }
:deep(.splide__arrow) {
  background: rgba(255,255,255,0.15);
  border: none;
}

/* Slide sizing */
.slide-content {
  position: relative;
  width: 100%;
}

/* Correct class */
.background-black { background: #000; }

/* Image: never show a white box while loading/decoding */
.slide-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;       /* if src not ready, stays black */
  /* prevent momentary white during decode/composition */
  contain: paint;
}

/* Text overlay */
.text-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #fff;
  text-align: center;
  background: rgba(0,0,0,0.3);
  padding: 20px;
}

.text-overlay :deep(h1),
.text-overlay :deep(h2),
.text-overlay :deep(h3) {
  font-size: clamp(2rem, 7vw, 4rem) !important;
  line-height: 1.2 !important;
  margin-bottom: 0.5rem;
}

.text-overlay :deep(p) {
  font-size: clamp(1.25rem, 3vw, 1.5rem) !important;
  line-height: 1.5 !important;
}
</style>
